import React, { useEffect, useState } from 'react';
import { trainingAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, GraduationCap, Edit, Trash2 } from 'lucide-react';

const CATEGORIES = ['technical', 'soft_skills', 'compliance', 'leadership', 'safety', 'product', 'other'];
const STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'];
const STATUS_COLORS = { scheduled: 'outline', in_progress: 'secondary', completed: 'default', cancelled: 'destructive' };

const defaultRec = { employee_id: '', title: '', provider: '', category: 'technical', start_date: '', end_date: '', status: 'scheduled', score: '', notes: '' };

const Training = () => {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmp, setFilterEmp] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultRec);
  const [acting, setActing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filterEmp && filterEmp !== 'all' ? { employee_id: filterEmp } : {};
      const [t, e] = await Promise.all([trainingAPI.list(params), employeesAPI.getAll()]);
      setRecords(t.data);
      setEmployees(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterEmp]);

  const openCreate = () => { setEditing(null); setForm(defaultRec); setCreateOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      employee_id: r.employee_id, title: r.title, provider: r.provider || '',
      category: r.category || 'technical', start_date: r.start_date || '', end_date: r.end_date || '',
      status: r.status || 'scheduled', score: r.score ?? '', notes: r.notes || ''
    });
    setCreateOpen(true);
  };

  const save = async () => {
    if (!form.employee_id || !form.title) { toast.error('Employee and title required'); return; }
    setActing(true);
    try {
      const payload = { ...form, score: form.score === '' ? null : parseFloat(form.score) };
      if (editing) {
        await trainingAPI.update(editing.id, payload);
        toast.success('Training updated');
      } else {
        await trainingAPI.create(payload);
        toast.success('Training assigned');
      }
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this training record?')) return;
    try { await trainingAPI.delete(id); toast.success('Deleted'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const summary = records.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap />Training & Development</h1>
          <p className="text-muted-foreground">Assign trainings, track progress and scores</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterEmp} onValueChange={setFilterEmp}>
            <SelectTrigger className="w-56" data-testid="training-filter-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="add-training-btn"><Plus size={16} className="mr-2" />Assign Training</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{records.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Scheduled</p><p className="text-2xl font-bold text-amber-600">{summary.scheduled || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">In Progress</p><p className="text-2xl font-bold text-blue-600">{summary.in_progress || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Completed</p><p className="text-2xl font-bold text-emerald-600">{summary.completed || 0}</p></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Title</TableHead><TableHead>Category</TableHead>
            <TableHead>Provider</TableHead><TableHead>Dates</TableHead><TableHead>Status</TableHead>
            <TableHead>Score</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {records.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No training records</TableCell></TableRow>}
            {records.map(r => (
              <TableRow key={r.id} data-testid={`training-row-${r.id}`}>
                <TableCell className="font-medium">{r.employee_name || '—'}<div className="text-xs text-muted-foreground">{r.employee_code}</div></TableCell>
                <TableCell>{r.title}</TableCell>
                <TableCell><Badge variant="outline">{r.category || '—'}</Badge></TableCell>
                <TableCell>{r.provider || '—'}</TableCell>
                <TableCell className="text-xs">{r.start_date || '—'}<br/>→ {r.end_date || '—'}</TableCell>
                <TableCell><Badge variant={STATUS_COLORS[r.status]}>{(r.status || '').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell>{r.score ?? '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-training-${r.id}`}><Edit size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(r.id)} data-testid={`del-training-${r.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Assign'} Training</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                <SelectTrigger data-testid="training-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="training-title" placeholder="React Advanced Patterns" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Provider</Label><Input value={form.provider} onChange={(e) => setForm({...form, provider: e.target.value})} placeholder="Coursera, in-house..." /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="training-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Score (0-100)</Label><Input type="number" value={form.score} onChange={(e) => setForm({...form, score: e.target.value})} /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={acting} data-testid="save-training-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}{editing ? 'Update' : 'Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Training;
